// LocationBindingCard — embeds in the Location edit drawer to manage
// NTAG ↔ location binding without leaving the page.
//
// States:
//   - bound:   show UID + bound_at + 「解除綁定」/「重新綁定」buttons
//   - unbound: 「產生配對碼」button → issues a 1h single-use code → show
//              the 8-char code with a live countdown + operator instructions
//   - issued:  show the active code; allow re-issuing a fresh one (which
//              invalidates the old one server-side)
//
// All visual tokens come from the existing dashboard design system
// (bg-bg-raised / text-text-primary / accent / etc.) so the card slots
// straight into the Drawer without restyling.

import React, { useEffect, useRef, useState } from "react";
import { Link, KeyRound, Unlink2, Copy, RefreshCw, AlertCircle } from "lucide-react";
import QRCode from "qrcode";
import {
  issuePairingCode, unbindLocation, rebindLocation, friendlyPairingError,
  type IssueCodeResult,
} from "../lib/pairingApi.js";
import { useConfirmDialog } from "./ConfirmDialog.js";

interface ActiveCode {
  code: string;
  expires_at: number;
}

interface Props {
  locationId: string;
  tenantId: string | null;
  initialUid: string | null;        // current bound UID, null if not bound
  initialBound: boolean;
  /** Called after a binding mutation succeeds (unbind / rebind) so the
   *  parent can refetch the location detail. */
  onChange?: () => void;
}

export default function LocationBindingCard({
  locationId, tenantId, initialUid, initialBound, onChange,
}: Props): React.ReactElement {
  const [bound, setBound] = useState(initialBound);
  const [uid, setUid] = useState(initialUid);
  const [active, setActive] = useState<ActiveCode | null>(null);
  const [busy, setBusy] = useState<"idle" | "issue" | "unbind" | "rebind">("idle");
  const [err, setErr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  // Live countdown for an issued code.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [active]);

  // Auto-clear the displayed code once it's expired (server enforces
  // separately; this is just UI hygiene).
  useEffect(() => {
    if (active && active.expires_at <= now) setActive(null);
  }, [active, now]);

  const dlg = useConfirmDialog();
  const issue = async () => {
    setBusy("issue"); setErr(null); setCopied(false);
    try {
      const r = await issuePairingCode(locationId, tenantId);
      setActive({ code: r.code, expires_at: r.expires_at });
    } catch (e) { setErr(friendlyPairingError(e)); }
    finally { setBusy("idle"); }
  };

  const unbind = async () => {
    if (!(await dlg.confirm("確定解除此地點的加密巡邏標籤綁定？\n\n歷史巡邏紀錄保留；解除後此加密巡邏標籤將無法用於本地點，需重新配對。", { destructive: true, okLabel: "解除綁定" }))) return;
    setBusy("unbind"); setErr(null);
    try {
      await unbindLocation(locationId, tenantId);
      setBound(false); setUid(null); setActive(null);
      onChange?.();
    } catch (e) { setErr(friendlyPairingError(e)); }
    finally { setBusy("idle"); }
  };

  const rebind = async () => {
    if (!(await dlg.confirm("重新綁定流程：\n1. 解除目前加密巡邏標籤綁定\n2. 產生新的 8 碼配對碼\n3. 拿新標籤到現場感應後輸入此配對碼\n\n確定執行？", { okLabel: "執行重新綁定" }))) return;
    setBusy("rebind"); setErr(null);
    try {
      const r = await rebindLocation(locationId, tenantId);
      setBound(false); setUid(null);
      setActive({ code: r.code, expires_at: r.expires_at });
      onChange?.();
    } catch (e) { setErr(friendlyPairingError(e)); }
    finally { setBusy("idle"); }
  };

  const copyCode = () => {
    if (!active) return;
    navigator.clipboard.writeText(active.code).then(
      () => { setCopied(true); setTimeout(() => setCopied(false), 2000); },
      () => { /* clipboard refused — leave UI as is */ },
    );
  };

  const remainingSec = active ? Math.max(0, Math.floor((active.expires_at - now) / 1000)) : 0;
  const remainingLabel = formatDuration(remainingSec);

  return (
    <section className="rounded-md border border-border bg-bg-raised p-4 space-y-3">
      <header className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-text-primary inline-flex items-center gap-2">
          <KeyRound className="h-4 w-4 text-accent" /> 加密巡邏標籤綁定
        </h3>
        <span className={`badge-default text-2xs ${bound ? "badge-success" : "badge-warning"}`}>
          {bound ? "已綁定" : "未綁定"}
        </span>
      </header>

      {bound && uid && (
        <div className="space-y-2">
          <p className="text-xs text-text-muted">目前綁定的加密巡邏標籤 UID：</p>
          <input className="input font-mono text-sm" disabled value={uid} />
          <div className="flex gap-2 pt-1">
            <button className="btn-secondary text-sm" onClick={() => void rebind()} disabled={busy !== "idle"}>
              <RefreshCw className={`h-4 w-4 ${busy === "rebind" ? "animate-spin" : ""}`} />
              重新綁定
            </button>
            <button className="btn-danger text-sm" onClick={() => void unbind()} disabled={busy !== "idle"}>
              <Unlink2 className="h-4 w-4" />
              {busy === "unbind" ? "解除中…" : "解除綁定"}
            </button>
          </div>
        </div>
      )}

      {!bound && !active && (
        <div className="space-y-2">
          <p className="text-xs text-text-secondary leading-relaxed">
            尚未綁定任何加密巡邏標籤。產生 8 碼配對碼後，將標籤帶到現場感應，於開啟的頁面輸入配對碼即可完成綁定。
          </p>
          <button className="btn-primary text-sm" onClick={() => void issue()} disabled={busy !== "idle"}>
            <Link className="h-4 w-4" />
            {busy === "issue" ? "產生中…" : "產生配對碼"}
          </button>
        </div>
      )}

      {!bound && active && (
        <div className="space-y-3">
          <p className="text-xs text-text-secondary leading-relaxed">
            請拿著加密巡邏標籤到現場感應，手機開啟的配對頁中輸入此配對碼：
          </p>
          <div className="rounded-md border border-accent/40 bg-accent-subtle p-3 flex items-center justify-between gap-2">
            <code className="font-mono text-2xl tracking-[0.3em] text-accent select-all">
              {active.code}
            </code>
            <button
              onClick={copyCode}
              className="btn-secondary text-xs px-2 py-1 inline-flex items-center gap-1"
              type="button"
            >
              <Copy className="h-3 w-3" /> {copied ? "已複製" : "複製"}
            </button>
          </div>
          <div className="text-2xs text-text-muted flex items-center justify-between">
            <span>剩餘有效時間：<span className="font-mono">{remainingLabel}</span></span>
            <button
              onClick={() => void issue()}
              className="text-accent hover:text-accent-hover transition-colors"
              type="button"
              disabled={busy !== "idle"}
            >
              重新產生
            </button>
          </div>
          <PairingQrPanel code={active.code} />
          <ol className="text-2xs text-text-muted space-y-1 list-decimal list-inside leading-relaxed">
            <li>拿加密巡邏標籤靠近手機背面 NFC 感應區</li>
            <li>手機自動開啟「綁定地點」頁面</li>
            <li>輸入上方配對碼，或點配對頁的「掃描」按鈕對著上方 QR 掃 → 完成</li>
          </ol>
        </div>
      )}

      {err && (
        <div className="rounded border border-danger/40 bg-danger/10 p-2 text-xs text-danger flex items-start gap-2">
          <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
          <span>{err}</span>
        </div>
      )}
    </section>
  );
}

function formatDuration(totalSec: number): string {
  if (totalSec <= 0) return "已過期";
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/** Renders the 8-char pairing code as a QR Code that the operator on
 *  the pair page can scan via the「掃描」 button to auto-fill the
 *  pairing-code input — saves them typing 8 chars on a phone keyboard.
 *
 *  The QR encodes the raw code string (8 chars, A-Z2-9). PairPage's
 *  scan button validates the decoded payload matches that shape
 *  before setCode-ing. Lock-ref pattern keeps QRCode.toDataURL from
 *  re-running on every render (only when the code itself changes). */
function PairingQrPanel({ code }: { code: string }): React.ReactElement {
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const qrLockRef = useRef<string | null>(null);
  useEffect(() => {
    if (qrLockRef.current === code) return;
    qrLockRef.current = code;
    QRCode.toDataURL(code, {
      errorCorrectionLevel: "M",
      margin: 1,
      width: 200,
      color: { dark: "#0a0a0c", light: "#ffffff" },
    }).then(setQrDataUrl).catch(() => setQrDataUrl(null));
  }, [code]);
  return (
    <div className="flex items-start gap-3 rounded-md border border-border-subtle bg-bg-raised p-3">
      <div className="bg-white p-2 rounded-md flex-shrink-0">
        {qrDataUrl ? (
          <img
            src={qrDataUrl}
            alt={`配對碼 QR：${code}`}
            width={144}
            height={144}
            className="block"
          />
        ) : (
          <div className="h-[144px] w-[144px] flex items-center justify-center text-2xs text-text-muted">
            產生中…
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0 text-2xs text-text-secondary leading-relaxed">
        <p className="font-medium text-text-primary mb-1">用手機掃這個就好</p>
        <p>在配對頁點「掃描」按鈕、對著這個 QR 拍 → 自動填入配對碼，省去手打 8 字。</p>
      </div>
    </div>
  );
}
